import { createTheme } from "@mui/material/styles";

/** Party shell default colors (see `party/partyShell.css`). Exported for typed `useTheme().palette` access. */
export const partyShellPalette = {
  bg: "#0e0b1a",
  bg2: "#17132a",
  ink: "#f6efe0",
  inkDim: "rgba(246, 239, 224, 0.62)",
  inkFaint: "rgba(246, 239, 224, 0.22)",
  paper: "#f1e7d0",
  paperInk: "#1a1423",
  hot: "#ff3d78",
  gold: "#ffc15e",
  danger: "#ff4d5f",
  violet: "#8a6cf1",
} as const;

export type PartyShellPalette = typeof partyShellPalette;

// Create a base theme to access breakpoints
const baseTheme = createTheme();

// Bright neon gamified theme matching card deck colors
const theme = createTheme({
  direction: "rtl", // RTL for Arabic
  palette: {
    mode: "light", // Changed to light mode for bright backgrounds
    party: { ...partyShellPalette },
    primary: {
      main: "#448AFF", // Bright Blue
      light: "#80B3FF",
      dark: "#2979FF",
    },
    secondary: {
      main: "#00E5CC", // Bright Cyan
      light: "#4DFFE6",
      dark: "#00BFA5",
    },
    error: {
      main: "#FF1744", // Bright Pink/Red
      light: "#FF6B9D",
      dark: "#D50000",
    },
    warning: {
      main: "#FFD740", // Bright Yellow
      light: "#FFE57F",
      dark: "#FFC400",
    },
    info: {
      main: "#18FFFF", // Bright Aqua
      light: "#64FFDA",
      dark: "#00BCD4",
    },
    success: {
      main: "#7C4DFF", // Bright Purple
      light: "#B388FF",
      dark: "#651FFF",
    },
    // Neon color palette for card decks
    neon: {
      pink: "#FF6B9D",
      pinkLight: "#FFB3D1",
      pinkDark: "#FF1744",
      cyan: "#00E5CC",
      cyanLight: "#4DFFE6",
      cyanDark: "#00BFA5",
      blue: "#448AFF",
      blueLight: "#80B3FF",
      blueDark: "#2979FF",
      yellow: "#FFD740",
      yellowLight: "#FFE57F",
      yellowDark: "#FFC400",
      purple: "#B388FF",
      purpleLight: "#D1C4E9",
      purpleDark: "#7C4DFF",
      aqua: "#18FFFF",
      aquaLight: "#64FFDA",
      aquaDark: "#00BCD4",
    } as any,
    background: {
      default: "#FFFFFF", // White background (gradient applied in components)
      paper: "#FFFFFF", // White cards/panels
      gradient: {
        start: "#E8F4F8",
        middle: "#F0F8FF",
        end: "#FFFFFF",
      },
      overlay: "rgba(100, 200, 255, 0.1)",
    } as any,
    text: {
      primary: "#1A237E", // Navy blue for bright backgrounds
      secondary: "#424242",
      disabled: "#9E9E9E",
      white: "#FFFFFF", // For dark elements
    } as any,
    border: {
      neon: "rgba(100, 200, 255, 0.4)",
      glow: "rgba(100, 200, 255, 0.15)",
    } as any,
  } as any,
  typography: {
    fontSize: 20, // 1rem = 16px
    fontFamily: [
      "Inter",
      "-apple-system",
      "BlinkMacSystemFont",
      '"Segoe UI"',
      "Roboto",
      '"Helvetica Neue"',
      "Arial",
      "sans-serif",
    ].join(","),
    h1: {
      fontWeight: 700,
      fontSize: "1.75rem", // xs
      [baseTheme.breakpoints.up("sm")]: {
        fontSize: "2rem", // sm
      },
      [baseTheme.breakpoints.up("md")]: {
        fontSize: "2.25rem", // md
      },
      [baseTheme.breakpoints.up("lg")]: {
        fontSize: "2.5rem", // lg
      },
      [baseTheme.breakpoints.up("xl")]: {
        fontSize: "3rem", // xl
      },
    },
    h2: {
      fontWeight: 700,
      fontSize: "1.5rem", // xs
      [baseTheme.breakpoints.up("sm")]: {
        fontSize: "1.75rem", // sm
      },
      [baseTheme.breakpoints.up("md")]: {
        fontSize: "1.875rem", // md
      },
      [baseTheme.breakpoints.up("lg")]: {
        fontSize: "2rem", // lg
      },
      [baseTheme.breakpoints.up("xl")]: {
        fontSize: "2.25rem", // xl
      },
    },
    h3: {
      fontWeight: 600,
      fontSize: "1.25rem", // xs
      [baseTheme.breakpoints.up("sm")]: {
        fontSize: "1.5rem", // sm
      },
      [baseTheme.breakpoints.up("md")]: {
        fontSize: "1.625rem", // md
      },
      [baseTheme.breakpoints.up("lg")]: {
        fontSize: "1.75rem", // lg
      },
      [baseTheme.breakpoints.up("xl")]: {
        fontSize: "2rem", // xl
      },
    },
    h4: {
      fontWeight: 600,
      fontSize: "1.125rem", // xs
      [baseTheme.breakpoints.up("sm")]: {
        fontSize: "1.125rem", // sm
      },
      [baseTheme.breakpoints.up("md")]: {
        fontSize: "1.125rem", // md
      },
      [baseTheme.breakpoints.up("lg")]: {
        fontSize: "1.125rem", // lg
      },
      [baseTheme.breakpoints.up("xl")]: {
        fontSize: "1.75rem", // xl
      },
    },
    h5: {
      fontWeight: 500,
      fontSize: "1rem", // xs
      [baseTheme.breakpoints.up("sm")]: {
        fontSize: "1.125rem", // sm
      },
      [baseTheme.breakpoints.up("md")]: {
        fontSize: "1.1875rem", // md
      },
      [baseTheme.breakpoints.up("lg")]: {
        fontSize: "1.25rem", // lg
      },
      [baseTheme.breakpoints.up("xl")]: {
        fontSize: "1.375rem", // xl
      },
    },
    h6: {
      fontWeight: 500,
      fontSize: "0.875rem", // xs
      [baseTheme.breakpoints.up("sm")]: {
        fontSize: "0.9375rem", // sm
      },
      [baseTheme.breakpoints.up("md")]: {
        fontSize: "0.96875rem", // md
      },
      [baseTheme.breakpoints.up("lg")]: {
        fontSize: "1rem", // lg
      },
      [baseTheme.breakpoints.up("xl")]: {
        fontSize: "1.125rem", // xl
      },
    },
    body1: {
      fontSize: "0.875rem", // xs
      fontWeight: 400,
      [baseTheme.breakpoints.up("sm")]: {
        fontSize: "0.9375rem", // sm
      },
      [baseTheme.breakpoints.up("md")]: {
        fontSize: "0.96875rem", // md
      },
      [baseTheme.breakpoints.up("lg")]: {
        fontSize: "1rem", // lg
      },
      [baseTheme.breakpoints.up("xl")]: {
        fontSize: "1.125rem", // xl
      },
    },
    body2: {
      fontSize: "0.75rem", // xs
      fontWeight: 400,
      [baseTheme.breakpoints.up("sm")]: {
        fontSize: "0.8125rem", // sm
      },
      [baseTheme.breakpoints.up("md")]: {
        fontSize: "0.84375rem", // md
      },
      [baseTheme.breakpoints.up("lg")]: {
        fontSize: "0.875rem", // lg
      },
      [baseTheme.breakpoints.up("xl")]: {
        fontSize: "1rem", // xl
      },
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: 24,
          padding: "10px 24px",
          fontWeight: 500,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          backgroundColor: "#FFFFFF",
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: "#FFFFFF",
          boxShadow: "none",
          borderBottom: "1px solid rgba(100, 200, 255, 0.4)",
        },
      },
    },
  },
});

export default theme;
